import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import ExpandableImageComponent from '../../Image/ExpandableImageComponent';

import { avatarPlaceholderSRC, checkHasPermissions } from '../../../utils';
import { getLinkToProperty, getLinkToUser } from '../../Messages/utils';
import { getFullName } from '../../Person/utils';

import permissions from '../../../permissions';
import { secondaryColor } from '../../../theme';
import WithLink from '../../Linkify';
import { PostHeader } from '../../Shared/post';
import StatusBadge from '../components/StatusBadge';

const StyledContainer = styled(Box)(() => ({
  display: 'flex',
  marginTop: '2rem',
  paddingBottom: '1.5rem',
  marginRight: 'auto',
  minWidth: '25rem',
  '&:last-child': {
    paddingBottom: '0',
  },
}));

const StyledMetaInfoPerson = styled(Box)(({ theme }) => ({
  display: 'flex',
  columnGap: '0.5rem',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  lineHeight: 1.1,
  marginTop: '-0.5rem',
  width: '60%',
  [theme.breakpoints.down('sm')]: {},
}));

const CommunityCommentComponent = ({
  comment,
  buildings,
  onRemoveComment,
  currentPostId,
}) => {
  const {
    creator,
    photo,
    createdAt,
    text,
    id,
    own: isOwnComment,
    approvalStatus,
  } = comment;

  const canDeleteComment =
    checkHasPermissions(permissions.delete_community_post) || isOwnComment;
  // Not sure if permissions to delete comments are different from permissions to delete posts

  const dropDownActions = [
    {
      label: 'Delete comment',
      onClick: () => onRemoveComment(currentPostId, id),
      color: secondaryColor,
      show: canDeleteComment,
    },
  ].filter(({ show }) => show);

  const personName = getFullName(creator);
  const linkToPerson = getLinkToUser(creator);
  const linkToProperty = getLinkToProperty(creator, buildings);
  const isInReview = approvalStatus === 'pending';

  return (
    <StyledContainer>
      <Box mr="1rem">
        <ExpandableImageComponent
          src={avatarPlaceholderSRC}
          photo={creator?.photo}
          name={personName}
          height="3.2rem"
          isSquare
        />
      </Box>

      <Box display="flex" flex="1" flexDirection="column">
        <Box display="flex" justifyContent="space-between">
          <StyledMetaInfoPerson>
            <Typography fontWeight="bold">
              {linkToPerson || personName}
            </Typography>
            {!!linkToProperty && <Box>{linkToProperty}</Box>}
          </StyledMetaInfoPerson>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            mt="-2rem"
          >
            <PostHeader
              dropDownActions={dropDownActions}
              createdAt={createdAt}
            />
          </Box>
        </Box>

        <Box mb="0.4rem">
          {text && (
            <Box whiteSpace="break-spaces">
              <WithLink>{text}</WithLink>
            </Box>
          )}
          {photo && (
            <ExpandableImageComponent photo={photo} height="10rem" isSquare />
          )}
          {isInReview && <StatusBadge>Comment in review</StatusBadge>}
        </Box>
      </Box>
    </StyledContainer>
  );
};

CommunityCommentComponent.propTypes = {
  comment: PropTypes.shape({
    creator: PropTypes.instanceOf(Object).isRequired,
    photo: PropTypes.instanceOf(Object),
    createdAt: PropTypes.string,
    text: PropTypes.string,
    approvalStatus: PropTypes.string,
    id: PropTypes.number,
    own: PropTypes.bool,
    requiresApproval: PropTypes.bool,
  }).isRequired,
  buildings: PropTypes.instanceOf(Object).isRequired,
  currentPostId: PropTypes.number.isRequired,
  onRemoveComment: PropTypes.func,
};

export default CommunityCommentComponent;
